import { ownerDocument } from 'dom-helpers';
import { Component } from 'react';
import * as ReactDOM from 'react-dom';
import EventListener from 'react-event-listener';

export interface IProps {
  children: any;
  /** One of 'onClick', 'onMouseDown', 'onMouseUp'. */
  mouseEvent?: string;
  /** One of 'onTouchStart', 'onTouchEnd'. */
  touchEvent?: string;
  onClickAway?: (e?: any) => void;
  enabled?: boolean;
}

/**
 * Listen for click events that are triggered outside of the component children.
 */
export class ClickAwayListener extends Component<IProps> {
  public static defaultProps: Pick<IProps, 'enabled' | 'mouseEvent' | 'touchEvent'> = {
    enabled: true,
    mouseEvent: 'onMouseUp',
    touchEvent: 'onTouchEnd',
  };
  private mounted = false;

  public render() {
    if (this.props.onClickAway && this.props.enabled) {
      const { children, mouseEvent, touchEvent, onClickAway, ...other } = this.props;
      const listenerProps = {};
      if (mouseEvent) {
        listenerProps[mouseEvent] = this.handleClickAway;
      }
      if (touchEvent) {
        listenerProps[touchEvent] = this.handleClickAway;
      }

      return (
        <EventListener target="document" {...listenerProps} {...other}>
          {children}
        </EventListener>
      );
    } else {
      return this.props.children;
    }
  }

  public componentDidMount() {
    this.mounted = true;
  }

  public componentWillUnmount() {
    this.mounted = false;
  }

  private handleClickAway = event => {
    // Ignore events that have been `event.preventDefault()` marked.
    if (event.defaultPrevented) {
      return;
    }

    // IE11 support, which trigger the handleClickAway even after the unbind
    if (!this.mounted) {
      return;
    }

    // eslint-disable-next-line react/no-find-dom-node
    const el = ReactDOM.findDOMNode(this).parentElement;
    const doc = ownerDocument(el);

    if (doc.documentElement && doc.documentElement.contains(event.target) && !isDescendant(el, event.target)) {
      this.props.onClickAway(event);
    }
  };
}

const isDescendant = (el, target) => {
  if (target !== null && target.parentNode) {
    return el === target || isDescendant(el, target.parentNode);
  }
  return false;
};
